import React, { memo, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import { ListArrow } from 'assets/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiProcessExecutionStatus } from 'constants/aiProcessRuns';

import { selectStepsByRun } from 'store/aiProcessRuns';

import { AiActivityTestIds } from '../AiActivity.testIds';
import {
  formatDurationSeconds,
  formatTimeOnly,
  getDurationSeconds,
  getRunStepCounts,
  humanizeOutcome,
  humanizeTrigger,
  resolveProcessName,
} from '../AiActivity.utils';
import ProcessColorChip from '../ProcessColorChip';
import RunFailureForensics from '../RunFailureForensics';
import StepMiniBars from '../StepMiniBars';

import RunFormTriageDot from './components/RunFormTriageDot';
import StatusIcon from './components/StatusIcon';
import {
  dotSeparatorStyles,
  expandToggleStyles,
  getRowWrapperStyles,
  rowBodyStyles,
  rowMainStyles,
  rowMetaStyles,
  rowTitleRowStyles,
  rowTrailingStyles,
} from './RunRow.styles';

const RunRow = ({ run, processesById, isExpanded, onExpandToggle, onOpenDrawer }) => {
  const processName = resolveProcessName(run, processesById);
  // Steps streamed into the store (live events, or a drawer load) are newer than
  // the list's REST counts, so the row's progress follows them when present.
  const steps = useSelector(
    useMemo(() => selectStepsByRun(run.id), [run.id]),
    shallowEqual,
  );
  const stepStatuses = useMemo(() => _.map(steps, 'status'), [steps]);
  const { total: totalSteps, finished: finishedSteps } = getRunStepCounts(run, stepStatuses);
  const durationSeconds = useMemo(() => getDurationSeconds(run), [run]);
  const isFailed = run.status === AiProcessExecutionStatus.FAILED;

  const onMainClick = useCallback(() => {
    onOpenDrawer(run.id);
  }, [onOpenDrawer, run.id]);

  const onExpandClick = useCallback(
    event => {
      event.stopPropagation();
      onExpandToggle(run.id);
    },
    [onExpandToggle, run.id],
  );

  return (
    <Box data-testid={AiActivityTestIds.RUN_ROW} sx={getRowWrapperStyles({ isFailed, isExpanded })}>
      <Box sx={rowMainStyles} onClick={onMainClick}>
        <StatusIcon status={run.status} />
        <Box sx={rowBodyStyles}>
          <Box sx={rowTitleRowStyles}>
            <ProcessColorChip name={processName} processId={run.aiProcessId} />
            <Typography variant="medium14">{humanizeOutcome(run, steps)}</Typography>
          </Box>
          <Box sx={rowMetaStyles}>
            <Typography variant="regular12">{humanizeTrigger(run)}</Typography>
            <Box sx={dotSeparatorStyles} />
            <Typography variant="regular12">{formatTimeOnly(run.startedAt)}</Typography>
            {durationSeconds !== null && (
              <>
                <Box sx={dotSeparatorStyles} />
                <Typography variant="regular12">
                  {formatDurationSeconds(durationSeconds)}
                </Typography>
              </>
            )}
            {totalSteps > 0 && (
              <>
                <Box sx={dotSeparatorStyles} />
                <Typography variant="regular12">
                  {finishedSteps} of {totalSteps} steps
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box sx={rowTrailingStyles}>
          <RunFormTriageDot executionId={run.id} />
          <StepMiniBars
            completedSteps={finishedSteps}
            status={run.status}
            stepStatuses={stepStatuses}
            totalSteps={totalSteps}
          />
          {isFailed && (
            <Box sx={expandToggleStyles} onClick={onExpandClick}>
              <ListArrow isExpanded={isExpanded} />
            </Box>
          )}
        </Box>
      </Box>
      {isFailed && isExpanded && (
        <RunFailureForensics
          data-testid={AiActivityTestIds.RUN_ROW_EXPANSION}
          errorMessage={run.errorMessage}
          errorReason={run.errorReason}
          recentEvents={run.recentEvents}
          recurringFailureCount={run.recurringFailureCount}
          runId={run.id}
        />
      )}
    </Box>
  );
};

RunRow.propTypes = {
  processesById: PropTypes.object.isRequired,
  run: PropTypes.object.isRequired,
  onExpandToggle: PropTypes.func.isRequired,
  onOpenDrawer: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
};

export default memo(RunRow);
